import React from 'react';

/**
 * Componente de estado para mostrar mensajes cuando no hay resultados de búsqueda
 * 
 * @description Proporciona feedback visual claro al usuario en dos escenarios:
 * estado inicial (sin búsqueda) y estado sin resultados (búsqueda sin coincidencias).
 * Implementa accesibilidad completa con ARIA live regions para anunciar cambios
 * de estado a lectores de pantalla de manera no intrusiva.
 * 
 * **Estados manejados:**
 * - **Estado inicial**: Query vacío - solicita al usuario escribir
 * - **Sin resultados**: Query con contenido - sugiere intentar otras palabras
 * 
 * **Características de accesibilidad:**
 * - `role="status"` para mensajes de estado
 * - `aria-live="polite"` para anuncios no intrusivos
 * - `aria-hidden="true"` en iconos decorativos
 * 
 * @component
 * @param {Object} props - Propiedades del componente
 * @param {string} [props.query=''] - Texto de búsqueda ingresado por el usuario
 * 
 * @returns {JSX.Element} Mensaje de estado con icono y texto apropiado
 * 
 * @example
 * // Estado inicial - usuario no ha escrito nada
 * <WithoutResults query="" />
 * // Renderiza: "Escriba su búsqueda arriba"
 * 
 * @example
 * // Estado sin resultados - búsqueda sin coincidencias
 * <WithoutResults query="termino inexistente" />
 * // Renderiza: "No se encontraron resultados. Intenta con otras palabras."
 * 
 * @example
 * // Integración con componente de búsqueda
 * const SearchResults = ({ query, results }) => {
 *   if (!results || results.length === 0) {
 *     return <WithoutResults query={query} />;
 *   }
 *   return <ResultsList results={results} />;
 * };
 * 
 * @example
 * // Uso en BuscadorApp
 * {!showLoading && !serviceError && (
 *   <Results data={data} query={query} />
 * )}
 * // Donde Results internamente usa WithoutResults cuando data está vacío
 * 
 * @example
 * // Manejo de espacios en blanco
 * <WithoutResults query="   " />  // Tratado como vacío
 * <WithoutResults query="\t\n" /> // Tratado como vacío
 * // Ambos muestran: "Escriba su búsqueda arriba"
 * 
 * @since 1.0.0
 * @author Valeria Garcia
 * 
 * @see {@link ../results/results.js} Componente que utiliza WithoutResults
 * @see {@link ../BuscadorApp.js} Aplicación principal que maneja estados
 * @see {@link https://www.w3.org/WAI/ARIA/apg/patterns/alert/} Guía ARIA para mensajes de estado
 */
export default function WithoutResults({ query = '' }) {
  const message =
    query.trim().length === 0
      ? 'Escriba su búsqueda arriba'
      : 'No se encontraron resultados. Intenta con otras palabras.';

  return (
    <div className="sin-resultados grid-news" role="status" aria-live="polite">
      <span className="icon-search" aria-hidden="true"></span>
      <span className="text-sin-resultados">{message}</span>
    </div>
  );
}